<template>
  <ArchiveDetailViewer
    v-if="selectedArchive"
    :archive="selectedArchive"
    @back="selectedArchive = null"
    @updated="handleArchiveUpdated"
  />

  <section v-else class="archive-dashboard">
    <div class="archive-toolbar">
      <form class="archive-search" @submit.prevent="searchArchives">
        <span class="material-icons-outlined" aria-hidden="true">search</span>
        <input
          v-model="searchEmail"
          type="email"
          placeholder="Search by email address"
          aria-label="Search archived App Clients by email address"
        />
        <button type="submit" :disabled="loading">
          {{ loading ? "Searching..." : "Search" }}
        </button>
        <button
          v-if="searchEmail || activeSearch"
          type="button"
          class="secondary-button"
          :disabled="loading"
          @click="clearSearch"
        >
          Clear
        </button>
      </form>

      <button type="button" class="refresh-button" :disabled="loading" @click="loadArchives">
        Refresh
      </button>
    </div>

    <div class="metric-grid">
      <article class="metric-card urgent">
        <span>Needs Review</span>
        <strong>{{ needsReviewCount }}</strong>
      </article>
      <article class="metric-card warning">
        <span>Open Matters</span>
        <strong>{{ openMatterCount }}</strong>
      </article>
      <article class="metric-card">
        <span>Action Required</span>
        <strong>{{ actionRequiredCount }}</strong>
      </article>
      <article class="metric-card">
        <span>{{ activeSearch ? "Search Results" : "Recent Archives" }}</span>
        <strong>{{ archives.length }}</strong>
      </article>
    </div>

    <div class="filter-row">
      <button
        v-for="filter in filters"
        :key="filter.value"
        type="button"
        :class="{ active: selectedFilter === filter.value }"
        @click="selectedFilter = filter.value"
      >
        {{ filter.label }}
      </button>
    </div>

    <div v-if="loading" class="state-card">Loading archived App Clients...</div>
    <div v-else-if="errorMessage" class="state-card error">{{ errorMessage }}</div>
    <div v-else-if="filteredArchives.length === 0" class="state-card">
      {{ activeSearch ? "No archived App Client matched that email address." : "No archives match this filter." }}
    </div>

    <div v-else class="archive-table">
      <div class="archive-row table-heading" aria-hidden="true">
        <span>App Client</span>
        <span>Archived</span>
        <span>Matters</span>
        <span>Review</span>
        <span>Action</span>
      </div>

      <button
        v-for="archive in filteredArchives"
        :key="archive.id"
        type="button"
        class="archive-row"
        @click="selectedArchive = archive"
      >
        <div class="identity">
          <strong>{{ archive.authEmail || archive.notificationEmail || "Email unavailable" }}</strong>
          <small>{{ archive.uid || archive.id }}</small>
        </div>
        <time>{{ formatDate(archive.archivedAt) }}</time>
        <span class="matter-pill" :class="{ open: archive.hasOpenMatters }">
          {{ archive.hasOpenMatters ? `${archive.openMatterCount || 0} Open` : "None Open" }}
        </span>
        <span class="status-pill" :class="archive.reviewStatus || 'pending'">
          {{ reviewLabel(archive.reviewStatus) }}
        </span>
        <span class="status-pill" :class="effectiveActionStatus(archive)">
          {{ actionLabel(effectiveActionStatus(archive)) }}
        </span>
      </button>
    </div>

    <p v-if="!activeSearch && archives.length >= 100" class="result-note">
      Showing the 100 most recently archived App Clients. Search by email to find an older archive.
    </p>
  </section>
</template>

<script>
import { computed, onMounted, ref } from "vue";

import { fetchArchivedClients } from "@/firebase/firestore";
import ArchiveDetailViewer from "@/components/archives/ArchiveDetailViewer.vue";

export default {
  name: "ArchiveDashboard",

  components: { ArchiveDetailViewer },

  setup() {
    const archives = ref([]);
    const selectedArchive = ref(null);
    const searchEmail = ref("");
    const activeSearch = ref("");
    const selectedFilter = ref("all");
    const loading = ref(false);
    const errorMessage = ref("");
    const filters = [
      { value: "all", label: "All" },
      { value: "needsReview", label: "Needs Review" },
      { value: "openMatters", label: "Open Matters" },
      { value: "actionRequired", label: "Action Required" }
    ];

    const needsReviewCount = computed(() => archives.value.filter(item => item.needsReview !== false).length);
    const openMatterCount = computed(() => archives.value.filter(item => item.hasOpenMatters === true).length);
    const effectiveActionStatus = archive => {
      return archive.actionStatus ?? (archive.hasOpenMatters ? "required" : "notRequired");
    };
    const actionRequiredCount = computed(() => archives.value.filter(item => {
      return ["required", "inProgress"].includes(effectiveActionStatus(item));
    }).length);
    const filteredArchives = computed(() => archives.value.filter(item => {
      if (selectedFilter.value === "needsReview") { return item.needsReview !== false; }
      if (selectedFilter.value === "openMatters") { return item.hasOpenMatters === true; }
      if (selectedFilter.value === "actionRequired") {
        return ["required", "inProgress"].includes(effectiveActionStatus(item));
      }

      return true;
    }));

    const loadArchives = async (email = "") => {
      loading.value = true;
      errorMessage.value = "";

      try {
        archives.value = await fetchArchivedClients({ email, maxResults: 100 });
        activeSearch.value = email;
      } catch (error) {
        console.error("❌ Unable to load archived App Clients:", error);
        errorMessage.value = error?.message ?? "Unable to load archived App Clients.";
      } finally {
        loading.value = false;
      }
    };

    const searchArchives = async () => {
      const email = searchEmail.value.trim().toLowerCase();

      if (!email) {
        await loadArchives();
        return;
      }

      await loadArchives(email);
    };

    const clearSearch = async () => {
      searchEmail.value = "";
      selectedFilter.value = "all";
      await loadArchives();
    };

    const handleArchiveUpdated = archive => {
      selectedArchive.value = archive;
      archives.value = archives.value.map(item => item.id === archive.id ? archive : item);
    };

    const dateValue = value => value?.toDate ? value.toDate() : new Date(value ?? 0);
    const formatDate = value => {
      const date = dateValue(value);
      return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric"
      });
    };
    const reviewLabel = status => ({ pending: "Pending", inReview: "In Review", reviewed: "Reviewed" }[status] ?? "Pending");
    const actionLabel = status => ({
      notRequired: "Not Required", required: "Required", inProgress: "In Progress", completed: "Completed"
    }[status] ?? "Not Required");

    onMounted(() => loadArchives());

    return {
      archives,
      selectedArchive,
      searchEmail,
      activeSearch,
      selectedFilter,
      loading,
      errorMessage,
      filters,
      needsReviewCount,
      openMatterCount,
      actionRequiredCount,
      filteredArchives,
      loadArchives,
      searchArchives,
      clearSearch,
      handleArchiveUpdated,
      formatDate,
      reviewLabel,
      actionLabel,
      effectiveActionStatus
    };
  }
};
</script>

<style scoped>
.archive-dashboard { display: grid; gap: 18px; }
.archive-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 14px; }
.archive-search { display: flex; align-items: center; gap: 8px; flex: 1; max-width: 760px; }
.archive-search > span { margin-right: -42px; z-index: 1; color: #75817b; }
.archive-search input { min-width: 240px; flex: 1; padding: 12px 14px 12px 42px; border: 1px solid #d8dfdb; border-radius: 10px; font: inherit; }
button { font: inherit; }
.archive-search button, .refresh-button { padding: 11px 18px; border: 1px solid #159c62; border-radius: 10px; background: #159c62; color: #fff; font-weight: 650; cursor: pointer; }
.archive-search .secondary-button, .refresh-button { border-color: #d8dfdb; background: #fff; color: #35423c; }
button:disabled { opacity: 0.6; cursor: default; }
.metric-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
.metric-card { padding: 18px; border: 1px solid #e0e5e2; border-radius: 14px; background: #fff; }
.metric-card span { display: block; color: #69746f; font-size: 0.82rem; font-weight: 650; text-transform: uppercase; letter-spacing: 0.05em; }
.metric-card strong { display: block; margin-top: 8px; font-size: 2rem; }
.metric-card.urgent { border-left: 4px solid #b54242; }
.metric-card.warning { border-left: 4px solid #d59827; }
.filter-row { display: flex; gap: 8px; flex-wrap: wrap; }
.filter-row button { padding: 8px 13px; border: 1px solid #dce2df; border-radius: 999px; background: #fff; color: #52605a; cursor: pointer; }
.filter-row button.active { border-color: #159c62; background: #edf8f2; color: #11794c; font-weight: 650; }
.state-card { padding: 28px; border: 1px solid #e0e5e2; border-radius: 14px; background: #f8faf9; text-align: center; color: #64716b; }
.state-card.error { border-color: #e6bcbc; background: #fff6f6; color: #9d3535; }
.archive-table { overflow: hidden; border: 1px solid #dfe5e1; border-radius: 14px; background: #fff; }
.archive-row { display: grid; grid-template-columns: minmax(240px, 2fr) 1fr 0.9fr 0.9fr 1fr; align-items: center; gap: 14px; width: 100%; padding: 16px 18px; border: 0; border-top: 1px solid #edf0ee; background: #fff; color: #26332d; text-align: left; cursor: pointer; }
.archive-row:not(.table-heading):hover { background: #f6faf7; }
.archive-row.table-heading { border-top: 0; background: #f4f7f5; color: #75817b; font-size: 0.74rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; cursor: default; }
.identity { display: grid; gap: 4px; min-width: 0; }
.identity strong, .identity small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.identity small { color: #7b8580; }
.matter-pill, .status-pill { width: fit-content; padding: 5px 9px; border-radius: 999px; background: #edf1ef; color: #53615a; font-size: 0.8rem; font-weight: 650; }
.matter-pill.open, .status-pill.required { background: #fff0e5; color: #a55218; }
.status-pill.pending { background: #fff4dd; color: #966817; }
.status-pill.inReview, .status-pill.inProgress { background: #e8f2ff; color: #27669b; }
.status-pill.reviewed, .status-pill.completed { background: #e5f6ed; color: #18764b; }
.result-note { margin: -7px 0 0; color: #77827d; font-size: 0.85rem; }
@media (max-width: 1050px) {
  .metric-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .archive-row { grid-template-columns: minmax(220px, 2fr) 1fr 0.9fr 1fr; }
  .archive-row > :nth-child(3) { display: none; }
}
@media (max-width: 720px) {
  .archive-toolbar, .archive-search { align-items: stretch; flex-direction: column; }
  .archive-search { max-width: none; }
  .archive-search > span { display: none; }
  .archive-search input { min-width: 0; padding-left: 14px; }
  .metric-grid { grid-template-columns: 1fr 1fr; }
  .archive-row { grid-template-columns: minmax(0, 1fr) auto; }
  .archive-row > :nth-child(2), .archive-row > :nth-child(3), .archive-row > :nth-child(5) { display: none; }
}
</style>
